import React, { useState } from 'react';
import './Checkout.css';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';
import emailjs from '@emailjs/browser';
import { db, doc, setDoc, increment, collection, getDoc } from '../firebase';

function Checkout() {
    const navigate = useNavigate();
    const { cartItems, clearCart } = useCart();
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        surname: '',
        email: '',
        phone: '',
        street: '',
        streetnumber: '',
        locality: '',
        province: '',
        postalcode: '',
        observations: '',
    });
    const [formErrors, setFormErrors] = useState({});

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });

        // Validar el campo correspondiente cada vez que el usuario escriba
        const fieldErrors = validationsForm({ ...formData, [name]: value });
        setFormErrors(fieldErrors);
    };


    const validationsForm = (form) => {
        let errors = {};

        // Expresiones regulares
        const regexName = /^[A-Za-zÑñÁáÉéÍíÓóÚúÜü\s]+$/;
        const regexEmail = /^(\w+[/./-]?){1,}@[a-z]+[/.]\w{2,}$/;
        const regexPhone = /^\d{10,15}$/;
        const regexStreetNumber = /^\d+$/;
        const regexPostalCode = /^\d{4,8}$/;

        // Validación de contacto
        if (!form.name.trim()) {
            errors.name = "El campo 'Nombre' es requerido";
        } else if (!regexName.test(form.name.trim())) {
            errors.name = "El campo 'Nombre' solo acepta letras y espacios en blanco";
        }

        if (!form.surname.trim()) {
            errors.surname = "El campo 'Apellido' es requerido";
        } else if (!regexName.test(form.surname.trim())) {
            errors.surname = "El campo 'Apellido' solo acepta letras y espacios en blanco";
        }

        if (!form.email.trim()) {
            errors.email = "El campo 'Correo electrónico' es requerido";
        } else if (!regexEmail.test(form.email.trim())) {
            errors.email = "El campo 'Correo electrónico' no es válido";
        }

        if (!form.phone.trim()) {
            errors.phone = "El campo 'Teléfono' es requerido";
        } else if (!regexPhone.test(form.phone.trim())) {
            errors.phone = "El campo 'Teléfono' debe contener entre 10 y 15 dígitos";
        }

        // Validación de envío
        if (!form.street.trim()) {
            errors.street = "El campo 'Calle' es requerido";
        }

        if (!form.streetnumber.trim()) {
            errors.streetnumber = "El campo 'Altura' es requerido";
        } else if (!regexStreetNumber.test(form.streetnumber.trim())) {
            errors.streetnumber = "El campo 'Altura' debe contener solo números";
        }

        if (!form.postalcode.trim()) {
            errors.postalcode = "El campo 'Código Postal' es requerido";
        } else if (!regexPostalCode.test(form.postalcode.trim())) {
            errors.postalcode = "El campo 'Código Postal' no es válido";
        }

        if (!form.locality.trim()) {
            errors.locality = "El campo 'Localidad/Barrio' es requerido";
        }

        if (!form.province.trim()) {
            errors.province = "El campo 'Provincia' es requerido";
        }

        return errors;
    };


    const prepareCartData = (cartItems) => {
        return cartItems.map(item => {
            const price = parseFloat(item.price); // Precio estándar
            const offerPrice = item["offer-price"] ? parseFloat(item["offer-price"]) : 0; // Precio de oferta
            const quantity = item.quantity || 1; // Cantidad

            return {
                name: item.name || "Sin nombre",
                price: price, // Precio estándar
                "offer-price": offerPrice || null, // Precio con oferta (null si no hay oferta)
                oldPrice: offerPrice ? price : null, // Mostrar precio original solo si hay oferta
                quantity: quantity, // Cantidad de artículos
                total: (offerPrice || price) * quantity, // Total con precio de oferta si aplica
                customization: {
                    colors: Array.isArray(item.customization?.colors)
                        ? item.customization.colors.map(color => color.name).join(", ")
                        : "No aplica",
                    size: item.customization?.size || "No aplica",
                    name: item.customization?.name || "No aplica",
                    comments: item.customization?.comments || "Sin comentarios"
                }
            };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        // Validar el formulario
        const formErrors = validationsForm(formData);
        if (Object.keys(formErrors).length > 0) {
            // Si existen errores, mostrar los mensajes de error
            console.log("Errores en el formulario:", formErrors);
            setLoading(false);
            return; // Detener el proceso si hay errores
        }

        // Si no hay errores, continuar con el proceso normal
        // Agrega el asunto al formData
        const enhancedFormData = {
            ...formData,
            _subject: 'Nuevo pedido de compra' // Asunto personalizado
        };

        try {
            // Obtener el último número de orden desde Firestore
            const orderNumberDocRef = doc(db, "orderNumbers", "currentOrderNumber");
            const orderNumberDocSnap = await getDoc(orderNumberDocRef);

            let formattedOrderNumber = '0000000001';
            if (orderNumberDocSnap.exists()) {
                const lastOrderNumber = orderNumberDocSnap.data().number;
                formattedOrderNumber = (lastOrderNumber + 1).toString().padStart(10, '0');
            }

            // Incrementar el número de orden en Firestore
            await setDoc(orderNumberDocRef, { number: increment(1) }, { merge: true });

            // Preparar los datos del carrito
            const preparedCartData = prepareCartData(cartItems);

            // Calcular totales
            const totalStandardPrice = preparedCartData.reduce((total, item) => total + (item.price * item.quantity), 0);
            const totalOfferPrice = preparedCartData.reduce((total, item) => total + ((item["offer-price"] || item.price) * item.quantity), 0);

            // Almacenar el pedido en Firestore
            const orderDocRef = doc(collection(db, "orders"), formattedOrderNumber);
            await setDoc(orderDocRef, {
                orderNumber: formattedOrderNumber,
                customerInfo: {
                    name: formData.name,
                    surname: formData.surname,
                    email: formData.email,
                    phone: formData.phone,
                    address: {
                        street: formData.street,
                        streetnumber: formData.streetnumber,
                        locality: formData.locality,
                        province: formData.province,
                        postalcode: formData.postalcode,
                    },
                    observations: formData.observations || "Sin observaciones",
                },
                cartDetails: preparedCartData.map(item => ({
                    name: item.name,
                    price: item.price, // Precio estándar
                    offerPrice: item["offer-price"], // Precio con oferta
                    oldPrice: item.price, // También puedes usar price como oldPrice explícitamente
                    quantity: item.quantity,
                    total: item.total,
                    customization: item.customization,
                })),
                totalPrice: totalOfferPrice,
                status: 'new',
                timestamp: new Date(),
            });

            // Preparar datos para FormSubmit
            const formSubmitData = {
                ...enhancedFormData, // Usar enhancedFormData aquí
                name: formData.name,
                surname: formData.surname,
                email: formData.email,
                phone: formData.phone,
                street: formData.street,
                streetnumber: formData.streetnumber,
                locality: formData.locality,
                province: formData.province,
                postalcode: formData.postalcode,
                observations: formData.observations || "Sin observaciones",
                orderNumber: formattedOrderNumber,
                cartDetails: JSON.stringify(preparedCartData, null, 2),
                totalPrice: `$${totalOfferPrice.toFixed(2)}`,
            };

            // Enviar datos a FormSubmit
            await fetch("https://formsubmit.co/ajax/pojobox.3d@gmail.com", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(formSubmitData),
            });

            // Enviar correo al cliente
            sendCustomerEmail(formData, preparedCartData, formattedOrderNumber);
            clearCart();
            navigate(`/thank-you/${formattedOrderNumber}`);
        } catch (error) {
            console.error("Error al procesar el pedido:", error);
        } finally {
            setLoading(false);
        }
    };


    // Función para enviar correo al cliente
    const sendCustomerEmail = (formData, preparedCartData, formattedOrderNumber) => {
        const serviceId = 'service_kz2p5x8';
        const templateId = 'template_rclpj8c';
        const userId = 'o7IXQ9U2winUyjXAr';

        // Formatear los detalles del pedido
        const orderDetailsText = preparedCartData.map((item, index) => {
            const priceBefore = item.oldPrice ? `$${item.oldPrice.toFixed(2)}` : null;
            const offerPrice = `$${(item["offer-price"] || item.price).toFixed(2)}`;
            const total = `$${item.total.toFixed(2)}`;

            return `Producto ${index + 1}:
            - Nombre: ${item.name}
            - Cantidad: ${item.quantity}
            - Precio actual: ${offerPrice}
            ${item.oldPrice ? `- Precio antes: ${priceBefore}` : ''} 
            - Total: ${total}`;
        }).join('\n\n');

        // Calcular el total sin oferta y el total con oferta
        const totalAmount = preparedCartData.reduce((total, item) => total + item.price * item.quantity, 0);
        const totalOfferAmount = preparedCartData.reduce((total, item) => total + (item.total), 0); // Solo si existe offer-price

        // Verificar si hay productos con oferta
        const hasOffer = preparedCartData.some(item => item["offer-price"]);

        // Elegir el total a enviar según si hay oferta o no
        const totalToSend = hasOffer ? totalOfferAmount : totalAmount;

        // Crear los parámetros de la plantilla
        const templateParams = {
            customer_name: formData.name,
            customer_email: formData.email,
            order_number: formattedOrderNumber,
            order_details: orderDetailsText,
            total: `$${totalToSend.toFixed(2)}`,  // Total final
        };

        // Enviar el correo usando EmailJS
        emailjs.send(serviceId, templateId, templateParams, userId)
            .then(response => {
                console.log('Email enviado exitosamente:', response.status, response.text);
            })
            .catch(error => {
                console.error('Error al enviar el email:', error);
            });
    };

    return (
        <div className="checkout">
            {loading && ( // Mostrar el loader si está activo
                <div className="loader-overlay">
                    <div className="loader"></div>
                </div>
            )}
            <h2>Finalizar Compra</h2>
            <form className="checkout-form" onSubmit={handleSubmit}>
                <h4>Información de contacto</h4>
                <div className="contact-info">
                    <div className='container-input'>
                        <label htmlFor="name">Nombre:</label>
                        <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                        />
                        {formErrors.name && <p className='error'>{formErrors.name}</p>}
                    </div>

                    <div className='container-input'>
                        <label htmlFor="surname">Apellido:</label>
                        <input
                            type="text"
                            id="surname"
                            name="surname"
                            value={formData.surname}
                            onChange={handleChange}
                            required
                        />
                        {formErrors.surname && <p className='error'>{formErrors.surname}</p>}
                    </div>

                    <div className='container-input'>
                        <label htmlFor="email">Correo electrónico:</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                        />
                        {formErrors.email && <p className='error'>{formErrors.email}</p>}
                    </div>
                    <div className='container-input'>
                        <label htmlFor="phone">Teléfono:</label>
                        <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                        />
                        {formErrors.phone && <p className='error'>{formErrors.phone}</p>}
                    </div>
                </div>
                <h4>Información de envío</h4>
                <div className="shipping-info">
                    <div className='container-input'>
                        <label htmlFor="street">Calle</label>
                        <input
                            type="text"
                            id="street"
                            name="street"
                            value={formData.street}
                            onChange={handleChange}
                            required
                        />
                        {formErrors.street && <p className='error'>{formErrors.street}</p>}
                    </div>
                    <div className='container-input'>
                        <label htmlFor="streetnumber">Altura</label>
                        <input
                            type="text"
                            id="streetnumber"
                            name="streetnumber"
                            value={formData.streetnumber}
                            onChange={handleChange}
                            required
                        />
                        {formErrors.streetnumber && <p className='error'>{formErrors.streetnumber}</p>}
                    </div>
                    <div className='container-input'>
                        <label htmlFor="postalcode">Código Postal</label>
                        <input
                            type="text"
                            id="postalcode"
                            name="postalcode"
                            value={formData.postalcode}
                            onChange={handleChange}
                            required
                        />
                        {formErrors.postalcode && <p className='error'>{formErrors.postalcode}</p>}
                    </div>
                    <div className='container-input'>
                        <label htmlFor="locality">Localidad/Barrio</label>
                        <input
                            type="text"
                            id="locality"
                            name="locality"
                            value={formData.locality}
                            onChange={handleChange}
                            required
                        />
                        {formErrors.locality && <p className='error'>{formErrors.locality}</p>}
                    </div>
                    <div className='container-input'>
                        <label htmlFor="province">Provincia</label>
                        <input
                            type="text"
                            id="province"
                            name="province"
                            value={formData.province}
                            onChange={handleChange}
                            required
                        />
                        {formErrors.province && <p className='error'>{formErrors.province}</p>}
                    </div>
                    <div className='container-input'>
                        <label htmlFor="observations">Observaciones</label>
                        <textarea
                            id="observations"
                            name="observations"
                            value={formData.observations}
                            onChange={handleChange}
                        />
                    </div>
                </div>
                <h4>Información de pago</h4>
                <div className="payment-info">
                    <p className="payment-notice">
                        Al confirmar tu compra, recibirás un correo electrónico con los detalles del alias para realizar la transferencia bancaria.
                    </p>
                    <div className="payment-method">
                        <p>Métodos de pago disponibles:</p>
                        <img src={require(`../assets/mercado-pago.webp`)} alt="Mercado Pago" />
                    </div>
                    <div className="payment-button">
                        <button type="submit">Finalizar</button>
                    </div>
                </div>
            </form>

        </div>
    );
}

export default Checkout;
